import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Save } from "lucide-react";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ExerciseSetForm } from "./ExerciseSetForm";
import { useAuth } from "@/components/AuthProvider";
import { useTranslations } from "@/hooks/useTranslations";
import { useLanguagePath } from "@/utils/language";

const trainingSessionSchema = z.object({
  student_id: z.string().min(1, "Selecione um aluno"),
  workout_id: z.string().min(1, "Selecione um treino"),
  workout_day: z.coerce.number().min(1, "Selecione um dia"),
  notes: z.string().optional()
});

type TrainingSessionFormValues = z.infer<typeof trainingSessionSchema>;

type StudentProfile = {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  trainer_id: string;
};

interface TrainingSessionFormProps {
  studentProfile?: StudentProfile | null;
}

// This list helps identify cardio exercises
const CARDIO_EXERCISE_KEYWORDS = [
  'correr', 'corrida', 'running', 'cardio', 'aeróbico', 'aerobic', 'pular corda', 'jump rope', 
  'esteira', 'treadmill', 'bicicleta', 'bike', 'cycling', 'elíptico', 'elliptical', 'nadar', 'natação',
  'swimming', 'caminhada', 'walking', 'step', 'hiit', 'circuito', 'circuit'
];

export const TrainingSessionForm = ({
  studentProfile
}: TrainingSessionFormProps) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { getPath } = useLanguagePath();
  const { t } = useTranslations("training-sessions");
  const {
    user
  } = useAuth();
  const [selectedWorkout, setSelectedWorkout] = useState<any>(null);
  const [exercises, setExercises] = useState<any[]>([]);
  const [dayTitles, setDayTitles] = useState<string[]>([]);
  
  const form = useForm<TrainingSessionFormValues>({
    resolver: zodResolver(trainingSessionSchema),
    defaultValues: {
      student_id: studentProfile?.id || "",
      workout_id: "",
      workout_day: 1,
      notes: ""
    }
  });

  useEffect(() => {
    if (studentProfile?.id) {
      form.setValue("student_id", studentProfile.id);
    }
  }, [studentProfile, form]);

  const {
    data: students
  } = useQuery({
    queryKey: ["students"],
    queryFn: async () => {
      const {
        data: {
          user: currentUser
        }
      } = await supabase.auth.getUser();
      const {
        data,
        error
      } = await supabase.from("students").select("*").eq("trainer_id", currentUser?.id).eq("status", "active").order("first_name");
      if (error) throw error;
      return data;
    },
    enabled: !studentProfile
  });

  const {
    data: workouts
  } = useQuery({
    queryKey: ["workouts", form.watch("student_id")],
    queryFn: async () => {
      const selectedStudentId = form.watch("student_id");
      if (!selectedStudentId) return null;
      console.log("Fetching workouts for student:", selectedStudentId);
      const {
        data,
        error
      } = await supabase.from("workouts").select("*").eq("target_student_id", selectedStudentId).eq("status", "active").order("title");
      if (error) throw error;
      console.log("Workouts loaded:", data);
      return data;
    },
    enabled: !!form.watch("student_id")
  });

  // Helper function to determine if an exercise is a cardio exercise
  const isCardioExercise = (exercise) => {
    const lowerName = exercise.name.toLowerCase();
    return CARDIO_EXERCISE_KEYWORDS.some(keyword => lowerName.includes(keyword.toLowerCase())) || 
           (exercise.body_part && exercise.body_part.toLowerCase() === 'cardio');
  };

  const fetchExercises = async (workoutId: string, day: number) => {
    try {
      // Step 1: Get the trainer_id associated with the workout
      const { data: workoutData, error: workoutError } = await supabase
        .from("workouts")
        .select("trainer_id")
        .eq("id", workoutId)
        .single();

      if (workoutError) {
        console.error("Error fetching workout trainer_id:", workoutError);
        toast.error(t("notifications.error_loading_workout_details"));
        return;
      }

      if (!workoutData?.trainer_id) {
        console.error("Trainer ID not found for workout:", workoutId);
        toast.error(t("notifications.error_trainer_not_found"));
        return;
      }
      const workoutTrainerId = workoutData.trainer_id;

      // Step 2: Fetch workout exercises and custom exercises (using the correct trainer_id)
      const [workoutExercisesResponse, customExercisesResponse] = await Promise.all([
        supabase
          .from("exercises")
          .select(`
            id,
            workout_id,
            day,
            sets,
            order_index,
            name,
            reps,
            weight,
            notes,
            image_url,
            body_part,
            target_muscle,
            instructions,
            time
          `)
          .eq("workout_id", workoutId)
          .eq("day", day)
          .order("order_index"),
        
        supabase
          .from("custom_exercises")
          .select("*")
          .eq("trainer_id", workoutTrainerId) // Use the fetched workoutTrainerId
      ]);

      if (workoutExercisesResponse.error) {
        console.error("Error fetching workout exercises:", workoutExercisesResponse.error);
        toast.error(t("notifications.error_loading_exercises"));
        return;
      }
      
      if (customExercisesResponse.error) {
        console.error("Error fetching custom exercises:", customExercisesResponse.error);
        toast.error(t("notifications.error_loading_exercises"));
        return;
      }

      const workoutExercises = workoutExercisesResponse.data || [];
      const customExercises = customExercisesResponse.data || [];

      console.log(`Fetching custom exercises for trainer ID: ${workoutTrainerId}`);
      console.log("Custom exercises found:", customExercises);
      console.log("Workout exercises found:", workoutExercises);

      // Create a map of custom exercise names for efficient lookup
      const customExerciseMap = new Map();
      customExercises.forEach(ce => {
        // Use trim() and toLowerCase() for robust matching
        const trimmedName = ce.name?.trim().toLowerCase();
        if (trimmedName) {
          customExerciseMap.set(trimmedName, ce);
        }
      });

      let exerciseList = workoutExercises.map(exercise => {
        // Make sure exercise is a valid object before processing
        if (!exercise || typeof exercise !== 'object') {
          console.error("Invalid exercise data:", exercise);
          return null;
        }
        
        // Check if this exercise name exists in custom exercises (using trim and toLowerCase)
        const trimmedExerciseName = exercise.name?.trim().toLowerCase();
        const customExercise = trimmedExerciseName ? customExerciseMap.get(trimmedExerciseName) : null;
        
        // Determine if this exercise is a cardio exercise
        const cardioExercise = isCardioExercise(exercise);

        console.log(`Checking exercise ${exercise.name}:`, {
          isCustomExercise: !!customExercise, // Alterado de isCustom para isCustomExercise
          isCardio: cardioExercise,
          customImageUrl: customExercise?.image_url,
          originalImageUrl: exercise.image_url
        });

        if (customExercise) {
          return {
            ...exercise,
            image_url: customExercise.image_url,
            isCustomExercise: true, // Alterado de isCustom para isCustomExercise
            isCardio: cardioExercise,
            body_part: customExercise.body_part || exercise.body_part,
            target_muscle: customExercise.target_muscle || exercise.target_muscle,
            instructions: customExercise.instructions || exercise.instructions
          };
        }
        return {
          ...exercise,
          isCustomExercise: false, // Alterado de isCustom para isCustomExercise
          isCardio: cardioExercise
        };
      }).filter(Boolean); // Filter out any null values from invalid data
      
      const exercisesWithSets = exerciseList.map(exercise => ({
        ...exercise,
        sets_data: Array.from({ length: exercise.sets }, (_, i) => ({
          set_number: i + 1,
          reps: "",
          weight: "",
          time: ""
        }))
      }));
      
      console.log("Final processed exercises:", exercisesWithSets);
      setExercises(exercisesWithSets);
    } catch (error) {
      console.error("Error fetching exercises:", error);
      toast.error(t("notifications.error_loading_exercises"));
    }
  };

  const handleWorkoutChange = async (workoutId: string) => {
    try {
      const {
        data,
        error
      } = await supabase.from("workouts").select("*").eq("id", workoutId).single();
      if (error) throw error;
      
      setSelectedWorkout(data);
      form.setValue("workout_id", workoutId);

      const { data: daysData, error: daysError } = await supabase
        .from("exercises")
        .select("day")
        .eq("workout_id", workoutId)
        .order("day");
      
      if (daysError) throw daysError;
      
      const uniqueDays = [...new Set(daysData?.map(ex => ex.day))].sort((a, b) => a - b);
      
      const titles = Array(Math.max(...uniqueDays, 0)).fill("").map((_, i) => `${t("form.day_prefix")} ${i+1}`);
      setDayTitles(titles);

      await fetchExercises(workoutId, 1);
    } catch (error) {
      console.error("Error fetching workout details:", error);
    }
  };

  const handleDayChange = async (day: number) => {
    const workoutId = form.getValues("workout_id");
    if (workoutId) {
      await fetchExercises(workoutId, day);
    }
    form.setValue("workout_day", day);
  };

  const updateExerciseSets = (exerciseIndex: number, sets: any[]) => {
    setExercises(prev => {
      const updated = [...prev];
      updated[exerciseIndex] = {
        ...updated[exerciseIndex],
        sets_data: sets,
        sets: sets.length // Update the number of sets if user added new ones
      };
      return updated;
    });
  };

  const onSubmit = async (data: TrainingSessionFormValues) => {
    try {
      const {
        data: {
          user: currentUser
        }
      } = await supabase.auth.getUser();
      if (!currentUser) throw new Error("Usuário não autenticado");
      console.log("Submitting training session with data:", data);

      const createdByType = studentProfile ? 'student' : 'trainer';

      const {
        data: session,
        error: sessionError
      } = await supabase.from("training_sessions").insert({
        student_id: data.student_id,
        trainer_id: currentUser.id,
        workout_id: data.workout_id,
        workout_day: data.workout_day,
        notes: data.notes,
        created_by_type: createdByType
      }).select().single();
      
      if (sessionError) throw sessionError;
      console.log("Training session created:", session);

      for (const exercise of exercises) {
        const isCardio = exercise.isCardio;
        
        const {
          data: exerciseSession,
          error: exerciseError
        } = await supabase.from("training_session_exercises").insert({
          training_session_id: session.id,
          exercise_id: exercise.id,
          sets_completed: exercise.sets_data?.length || exercise.sets,
          notes: exercise.notes,
          is_cardio: isCardio
        }).select().single();
        
        if (exerciseError) throw exerciseError;

        if (exercise.sets_data && exercise.sets_data.length > 0) {
          const setRecords = exercise.sets_data.map(set => ({
            training_session_exercise_id: exerciseSession.id,
            set_number: set.set_number,
            reps: set.reps ? parseInt(set.reps, 10) || null : null,
            weight: !isCardio && set.weight ? parseFloat(set.weight) || null : null,
            time: isCardio && set.time ? set.time : null,
            notes: set.notes
          }));
          
          const {
            error: setsError
          } = await supabase.from("training_session_exercise_sets").insert(setRecords);
          if (setsError) throw setsError;
        }
      }
      toast.success(t("notifications.created"));
      queryClient.invalidateQueries({
        queryKey: ["training-sessions"]
      });

      if (studentProfile) {
        navigate(getPath("/student-dashboard/training-sessions"));
      } else {
        navigate(getPath("/dashboard/training-sessions"));
      }
    } catch (error: any) {
      console.error("Error creating training session:", error);
      toast.error("Erro ao criar aula");
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <Card className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-lg border border-slate-100/50 dark:border-gray-700/50">
          <CardHeader>
            <CardTitle className="dark:text-white">{t("form.title")}</CardTitle>
            <CardDescription className="dark:text-gray-300">
              {t("form.description")}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {!studentProfile && (
                <FormField 
                  control={form.control} 
                  name="student_id" 
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="dark:text-white">{t("form.student")}</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger className="bg-white dark:bg-gray-800">
                            <SelectValue placeholder={t("form.selectStudent")} className="dark:text-gray-300" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-lg z-50">
                          {students?.map(student => (
                            <SelectItem key={student.id} value={student.id} className="dark:text-white">
                              {student.first_name} {student.last_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage className="dark:text-red-400" />
                    </FormItem>
                  )} 
                />
              )}

              <FormField 
                control={form.control} 
                name="workout_id" 
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="dark:text-white">{t("form.workout")}</FormLabel>
                    <Select onValueChange={handleWorkoutChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger className="bg-white dark:bg-gray-800">
                          <SelectValue placeholder={t("form.selectWorkout")} className="dark:text-gray-300" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-lg z-50">
                        {workouts?.map(workout => (
                          <SelectItem key={workout.id} value={workout.id} className="dark:text-white">
                            {workout.title}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage className="dark:text-red-400" />
                  </FormItem>
                )} 
              />

              {selectedWorkout && (
                <FormField 
                  control={form.control} 
                  name="workout_day" 
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="dark:text-white">{t("form.workoutDay")}</FormLabel>
                      <Select onValueChange={value => handleDayChange(Number(value))} value={String(field.value)}>
                        <FormControl>
                          <SelectTrigger className="bg-white dark:bg-gray-800">
                            <SelectValue placeholder={t("form.selectDay")} className="dark:text-gray-300" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-lg z-50 max-h-80">
                          {Array.from({
                            length: selectedWorkout.days
                          }, (_, i) => {
                            const dayIndex = i + 1;
                            return (
                              <SelectItem key={dayIndex} value={String(dayIndex)} className="dark:text-white">
                                <div className="flex flex-col">
                                  <span className="font-medium">{t("form.day_prefix")} {dayIndex}</span>
                                </div>
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                      <FormMessage className="dark:text-red-400" />
                    </FormItem>
                  )} 
                />
              )}

              <FormField 
                control={form.control} 
                name="notes" 
                render={({ field }) => (
                  <FormItem className="col-span-full">
                    <FormLabel className="dark:text-white">{t("form.notes")}</FormLabel>
                    <FormControl>
                      <Textarea {...field} placeholder={t("form.notesPlaceholder")} className="bg-white dark:bg-gray-800 dark:text-white" />
                    </FormControl>
                    <FormMessage className="dark:text-red-400" />
                  </FormItem>
                )} 
              />
            </div>
          </CardContent>
        </Card>

        {exercises.length > 0 && (
          <Card className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-lg border border-slate-100/50 dark:border-gray-700/50">
            <CardHeader>
              <CardTitle className="dark:text-white">{t("form.exercisesTitle")}</CardTitle>
              <CardDescription className="dark:text-gray-300">
                {t("form.exercisesDescription")}
              </CardDescription>
            </CardHeader>
            <CardContent className="px-0 sm:px-6">
              <div className="space-y-6">
                {exercises.map((exercise, index) => (
                  <Card key={exercise.id} className="bg-muted/50 dark:bg-gray-700/50 mx-0 sm:mx-0 dark:border-gray-600/50">
                    <CardContent className="px-2 sm:px-4 py-0">
                      <ExerciseSetForm 
                        totalSets={exercise.sets} 
                        onSetsChange={sets => updateExerciseSets(index, sets)} 
                        exerciseName={exercise.name}
                        exerciseDetails={{
                          sets: exercise.sets,
                          reps: exercise.reps,
                          weight: exercise.weight,
                          time: exercise.time
                        }} 
                        imageUrl={exercise.image_url}
                        instructions={exercise.instructions}
                        isCustomExercise={exercise.isCustomExercise}
                        isCardio={exercise.isCardio}
                      />
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        <div className="flex justify-end">
          <Button type="submit">
            <Save className="mr-2 h-4 w-4" />
            {t("button.save")}
          </Button>
        </div>
      </form>
    </Form>
  );
};
